import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import DOMPurify from "dompurify";
import { ArrowRight, BookOpen, PlayCircle } from "lucide-react";
import AdminActions from "@/components/AdminActions";
import ShareButton from "@/components/ShareButton";
import VideoEmbed from "@/components/VideoEmbed";
import { fontClass } from "@/lib/fonts";
import LangToggle from "@/components/LangToggle";
import Seo from "@/components/Seo";

function ReadingProgress() {
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setP(total > 0 ? Math.min(1, el.scrollTop / total) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div className="fixed top-16 left-0 right-0 h-1 z-40 bg-white/5" data-testid="reading-progress">
      <div
        className="h-full bg-gradient-to-r from-orange-400 via-pink-500 to-purple-500"
        style={{ width: `${p * 100}%`, boxShadow: "0 0 10px rgba(255,61,119,0.6)" }}
        data-testid="reading-progress-fill"
      />
    </div>
  );
}

export default function LessonDetail() {
  const { slug } = useParams();
  const [lesson, setLesson] = useState(null);
  const [all, setAll] = useState([]);
  const [lang, setLang] = useState("en");

  const load = useCallback(() => {
    api.get(`/lessons/${slug}`).then(r => setLesson(r.data)).catch(() => {});
    api.get("/lessons").then(r => setAll(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);

  if (!lesson) return <div className="min-h-screen flex items-center justify-center text-slate-200">Loading…</div>;

  const related = all.filter(l => l.slug !== lesson.slug && l.category === lesson.category).slice(0, 3);
  const fallbackRelated = related.length ? related : all.filter(l => l.slug !== lesson.slug).slice(0, 3);

  const hasBn = Boolean(lesson.title_bn);
  const bn = lang === "bn" && hasBn;
  const title = bn ? lesson.title_bn : lesson.title;
  const excerpt = bn ? (lesson.excerpt_bn || lesson.excerpt) : lesson.excerpt;
  const content = bn ? (lesson.content_bn || lesson.content) : lesson.content;

  return (
    <div className="min-h-screen pt-28">
      <Seo title={lesson.title} description={lesson.excerpt} path={`/lessons/${lesson.slug}`} type="article" />
      <ReadingProgress />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <Link to="/lessons" className="text-xs font-bold uppercase tracking-[0.14em] text-slate-200 hover:text-orange-300 transition-colors">← All Lessons</Link>

        <div className="mt-8 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="chip">{lesson.category}</span>
            <span className="text-sm text-slate-200">
              {new Date(lesson.published_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
          </div>
          <div className="flex items-center gap-3">
            {hasBn && <LangToggle lang={lang} setLang={setLang} />}
            <ShareButton url={`/api/share/lessons/${lesson.slug}`} variant="button" testId={`share-lesson-${lesson.slug}`} />
            <AdminActions kind="lessons" item={lesson} onChange={load} redirectAfterDelete="/lessons" />
          </div>
        </div>

        <h1 className={`${bn ? "font-bn" : fontClass(lesson.title_font, "font-bn")} mt-5 font-bold text-3xl md:text-4xl leading-snug tracking-tight text-white`} data-testid="lesson-title">
          {title}
        </h1>
        {excerpt && (
          <p className="mt-5 text-lg text-slate-200 font-bn leading-relaxed border-l-4 border-orange-400/30 pl-4">{excerpt}</p>
        )}

        {lesson.video_url ? (
          <div className="mt-10">
            <VideoEmbed url={lesson.video_url} />
          </div>
        ) : lesson.cover_image ? (
          <div className="mt-10 aspect-[16/9] overflow-hidden rounded-2xl border border-white/10 shadow-sm">
            <img src={lesson.cover_image} alt="" className="w-full h-full object-cover" />
          </div>
        ) : null}

        <div className="mt-10">
          {/<[a-z][\s\S]*>/i.test(content) ? (
            <div
              className="rich-content font-bn text-lg"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
              data-testid="lesson-content"
            />
          ) : (
            <div className="font-bn text-slate-200 leading-[1.9] text-lg whitespace-pre-line" data-testid="lesson-content">
              {content}
            </div>
          )}
        </div>
      </article>

      {/* Related lessons */}
      {fallbackRelated.length > 0 && (
        <section className="border-t border-white/10 py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-2 overline-label mb-8"><BookOpen size={13} /> More Lessons</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {fallbackRelated.map((l) => (
                <Link key={l.id} to={`/lessons/${l.slug}`} className="card-void overflow-hidden block group flex flex-col" data-testid={`related-lesson-${l.slug}`}>
                  <div className="aspect-[16/9] overflow-hidden relative bg-white/10">
                    {l.cover_image ? (
                      <img src={l.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"><BookOpen className="text-slate-200" size={30} /></div>
                    )}
                    {l.video_url && (
                      <div className="absolute inset-0 flex items-center justify-center bg-slate-900/25">
                        <span className="w-11 h-11 rounded-full bg-white/95 shadow flex items-center justify-center">
                          <PlayCircle className="text-orange-400" size={24} />
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <span className="chip mb-2.5">{l.category}</span>
                    <h3 className={`${fontClass(l.title_font, "font-bn")} font-bold text-base text-white leading-snug group-hover:text-orange-300 transition-colors`}>{l.title}</h3>
                    <div className="mt-3 text-orange-400 text-xs font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                      Read <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
